import { useState, type FormEvent } from 'react'
import type { CreateAppointmentResponse } from '@fitting-room/contracts'
import { Check, Copy } from 'lucide-react'
import { useAppointmentTypes, useAssignees, useBackOffice, useCreateAppointment } from '../../lib/adminQueries'
import { formatMinutes, minorFromDollars, offeredTypes } from '../../lib/appointments'
import { boutiqueDateTime } from '../../lib/boutique'
import { useStaffToken } from '../../lib/staffToken'
import { AssigneeSelect } from './AssigneeSelect'
import { DatePicker } from './DatePicker'
import { FormField, FormSheet } from './FormSheet'
import { TimeSelect } from './TimeSelect'
import { LENGTH_CHOICES } from './RescheduleDialog'

/** Loose on purpose: the API has the real rule, this only catches the obvious typo while the person is still at the keyboard. */
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/**
 * A walk-in, or a phone call, booked at the counter. Ported from tperent's
 * `NewAppointmentDialog`, less what Koalendar owns here: there is no slot
 * grid and no capacity check, just the time the customer and the stylist
 * agreed on.
 *
 * Booking opens the appointment's fitting room, so success is not "close the
 * sheet" but "here is the link to text them" — it is minted once, and this is
 * the moment somebody is standing there to send it.
 */
export function NewAppointmentDialog({
  open,
  onClose,
  initialDate,
  onOpenAppointment,
}: {
  readonly open: boolean
  readonly onClose: () => void
  readonly initialDate: string
  readonly onOpenAppointment: (visitId: string) => void
}) {
  return (
    <FormSheet
      open={open}
      onOpenChange={(next) => (next ? undefined : onClose())}
      title="New appointment"
      description="A walk-in or a phone call. Online bookings come in from Koalendar by themselves."
    >
      {/* Keyed on the day so opening it from another day is a fresh form. */}
      {open ? <NewAppointmentForm key={initialDate} initialDate={initialDate} onClose={onClose} onOpenAppointment={onOpenAppointment} /> : null}
    </FormSheet>
  )
}

function NewAppointmentForm({
  initialDate,
  onClose,
  onOpenAppointment,
}: {
  readonly initialDate: string
  readonly onClose: () => void
  readonly onOpenAppointment: (visitId: string) => void
}) {
  const { token } = useStaffToken()
  const types = offeredTypes(useAppointmentTypes(token).data?.types ?? [])
  const members = useAssignees(token).data?.members ?? []
  const backOffice = useBackOffice(token).data
  const sources = backOffice?.bookingSources ?? []
  const create = useCreateAppointment(token)

  const [tierKey, setTierKey] = useState('')
  const [date, setDate] = useState(initialDate)
  const [time, setTime] = useState('')
  const [length, setLength] = useState('')
  const [name, setName] = useState('')
  const [phone, setPhone] = useState('')
  const [email, setEmail] = useState('')
  const [guests, setGuests] = useState('')
  const [assigneeId, setAssigneeId] = useState<string | null>(null)
  const [source, setSource] = useState('walk_in')
  const [weddingDate, setWeddingDate] = useState('')
  const [budget, setBudget] = useState('')
  const [notes, setNotes] = useState('')
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [booked, setBooked] = useState<CreateAppointmentResponse | null>(null)

  // The first type until somebody picks one: most walk-ins are the shop's first appointment type.
  const chosenTier = tierKey || types[0]?.key || ''
  const tier = types.find((t) => t.key === chosenTier)
  const chosenSource = sources.some((s) => s.key === source) ? source : ''

  if (booked) return <Booked booked={booked} onClose={onClose} onOpenAppointment={onOpenAppointment} />

  const submit = (event: FormEvent) => {
    event.preventDefault()
    const next: Record<string, string> = {}
    if (!chosenTier) next.tierKey = 'Pick an appointment type.'
    if (!date) next.date = 'Pick a date.'
    if (!time) next.time = 'Pick a time.'
    if (!name.trim()) next.name = 'A name, so the appointment can be found again.'
    if (email.trim() && !EMAIL.test(email.trim())) next.email = "That doesn't look like an email address."
    const guestCount = guests.trim() === '' ? undefined : Number(guests)
    if (guestCount !== undefined && (!Number.isInteger(guestCount) || guestCount < 0 || guestCount > 30)) {
      next.guests = 'A number of guests, 0 to 30.'
    }
    const budgetMinor = minorFromDollars(budget)
    if (budgetMinor === undefined) next.budget = 'An amount in dollars, like 2,500.'
    setErrors(next)
    if (Object.keys(next).length > 0) return

    create.mutate(
      {
        tierKey: chosenTier,
        date,
        time,
        customerName: name.trim(),
        ...(length ? { durationMinutes: Number(length) } : {}),
        ...(phone.trim() ? { customerPhone: phone.trim() } : {}),
        ...(email.trim() ? { customerEmail: email.trim() } : {}),
        ...(guestCount !== undefined ? { guestCount } : {}),
        ...(assigneeId ? { assigneeId } : {}),
        ...(chosenSource ? { source: chosenSource } : {}),
        ...(weddingDate ? { weddingDate } : {}),
        ...(budgetMinor !== null && budgetMinor !== undefined ? { budgetMinor } : {}),
        ...(notes.trim() ? { staffNotes: notes.trim() } : {}),
      },
      { onSuccess: setBooked },
    )
  }

  return (
    <form onSubmit={submit} noValidate>
      <FormField label="Appointment type" error={errors.tierKey}>
        <select className="st-select" value={chosenTier} onChange={(event) => setTierKey(event.target.value)}>
          {types.map((type) => (
            <option key={type.key} value={type.key}>
              {type.label} · {formatMinutes(type.durationMinutes)}
            </option>
          ))}
        </select>
      </FormField>

      <div className="mb-3 flex flex-wrap items-start gap-3">
        <div className="flex flex-col gap-1">
          <span className="st-eyebrow">Date</span>
          <DatePicker value={date} onChange={setDate} label="Date" />
          {errors.date ? <span className="text-xs text-[color:#8d3f2c]">{errors.date}</span> : null}
        </div>
        <div className="basis-full">
          <FormField label="Time" error={errors.time}>
            <TimeSelect value={time} onChange={setTime} presets={backOffice?.appointmentTimes ?? []} />
          </FormField>
        </div>
        <FormField label="Length">
          <select className="st-select" value={length} onChange={(event) => setLength(event.target.value)}>
            <option value="">{tier ? `Usual (${formatMinutes(tier.durationMinutes)})` : 'Usual'}</option>
            {LENGTH_CHOICES.map((minutes) => (
              <option key={minutes} value={minutes}>
                {formatMinutes(minutes)}
              </option>
            ))}
          </select>
        </FormField>
      </div>

      <FormField label="Customer name" error={errors.name}>
        <input className="st-input" value={name} onChange={(event) => setName(event.target.value)} autoComplete="off" />
      </FormField>
      <div className="grid grid-cols-2 gap-3">
        <FormField label="Phone">
          <input className="st-input" type="tel" value={phone} onChange={(event) => setPhone(event.target.value)} autoComplete="off" />
        </FormField>
        <FormField label="Guests" error={errors.guests}>
          <input className="st-input" inputMode="numeric" value={guests} onChange={(event) => setGuests(event.target.value)} />
        </FormField>
      </div>
      <FormField label="Email" error={errors.email} hint="Where their reminders go. Without one, nobody can remind them.">
        <input className="st-input" type="email" value={email} onChange={(event) => setEmail(event.target.value)} autoComplete="off" />
      </FormField>

      <div className="grid grid-cols-2 gap-3">
        <FormField label="Bridal stylist">
          <AssigneeSelect members={members} value={assigneeId} onChange={setAssigneeId} />
        </FormField>
        <FormField label="Source">
          <select className="st-select" value={chosenSource} onChange={(event) => setSource(event.target.value)}>
            <option value="">Not recorded</option>
            {sources.map((entry) => (
              <option key={entry.key} value={entry.key}>
                {entry.label}
              </option>
            ))}
          </select>
        </FormField>
      </div>

      <div className="mb-3 flex flex-col gap-1">
        <span className="st-eyebrow">Wedding date</span>
        <div className="flex items-center">
          <DatePicker value={weddingDate} onChange={setWeddingDate} label="Wedding date" placeholder="Not set" onClear={() => setWeddingDate('')} />
        </div>
      </div>
      <FormField label="Budget" error={errors.budget}>
        <input className="st-input" inputMode="decimal" placeholder="$" value={budget} onChange={(event) => setBudget(event.target.value)} />
      </FormField>
      <FormField label="Notes">
        <textarea className="st-input" rows={3} value={notes} onChange={(event) => setNotes(event.target.value)} />
      </FormField>

      {create.isError ? (
        <p className="st-alert mb-3" role="alert">
          {create.error.message}
        </p>
      ) : null}

      <div className="mt-5 flex gap-2">
        <button type="button" className="st-btn-ghost flex-1" onClick={onClose}>
          Cancel
        </button>
        <button type="submit" className="st-btn flex-[2]" disabled={create.isPending}>
          {create.isPending ? 'Booking…' : 'Book appointment'}
        </button>
      </div>
    </form>
  )
}

function Booked({
  booked,
  onClose,
  onOpenAppointment,
}: {
  readonly booked: CreateAppointmentResponse
  readonly onClose: () => void
  readonly onOpenAppointment: (visitId: string) => void
}) {
  return (
    <div>
      <p className="st-ok mb-4" role="status">
        Booked {booked.visit.customerName} for {boutiqueDateTime(booked.visit.scheduledAt)}.
      </p>
      {booked.url ? <LinkToSend url={booked.url} /> : null}
      <div className="mt-5 flex gap-2">
        <button type="button" className="st-btn-ghost flex-1" onClick={onClose}>
          Done
        </button>
        <button type="button" className="st-btn flex-1" onClick={() => onOpenAppointment(booked.visit.id)}>
          Open appointment
        </button>
      </div>
    </div>
  )
}

/**
 * A fitting room link, ready to text. Shown only when it was just minted —
 * none is stored readable, so this is the one time it can be shown.
 */
export function LinkToSend({ url }: { readonly url: string }) {
  const [copied, setCopied] = useState(false)
  const copy = () => {
    void navigator.clipboard?.writeText(url).then(
      () => setCopied(true),
      () => setCopied(false),
    )
  }
  return (
    <div className="st-card px-3 py-3">
      <div className="st-eyebrow mb-1">Their fitting room link</div>
      <p className="st-meta mb-2 text-xs">Text it to them. It opens their fitting room — no sign-in needed.</p>
      <div className="flex items-center gap-2">
        <input className="st-input min-w-0 flex-1 text-xs" readOnly value={url} onFocus={(event) => event.currentTarget.select()} aria-label="Fitting room link" />
        <button type="button" className="st-btn-ghost" onClick={copy}>
          {copied ? <Check size={14} aria-hidden /> : <Copy size={14} aria-hidden />}
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
    </div>
  )
}
